import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, CardContent, Button, Grid, Alert, CircularProgress, Tabs, Tab, Stack, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller } from "react-hook-form";
import { z } from "zod";
import { useGetConnectedProvidersQuery, useDisconnectProviderMutation } from '../services/externalAuthApi';
import {
  useDeleteAlpacaTradingSettingsMutation,
  useGetAlpacaTradingSettingsQuery,
  useSaveAlpacaTradingSettingsMutation,
} from '../services/tradingSettingsApi';
import { useAppSelector } from '../store/hooks';
import useZodForm from '../components/form/useZodForm';
import SectionHeader from '../components/common/SectionHeader';
import AccentCard from "../components/common/AccentCard";

const alpacaCredentialsSchema = z.object({
  apiKey: z.string().trim().min(1, "API key is required"),
  apiSecret: z.string().max(200, "API secret is too long").optional(),
});

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [provider, setProvider] = useState('');
  const [alpacaMessage, setAlpacaMessage] = useState('');
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors: alpacaErrors },
  } = useZodForm({
    schema: alpacaCredentialsSchema,
    defaultValues: { apiKey: "", apiSecret: "" },
  });

  // Get authentication state
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  // RTK Query hooks
  const { data: connectedProviders, isLoading: isLoadingProviders, refetch: refetchProviders } = 
    useGetConnectedProvidersQuery(undefined, { skip: !isAuthenticated });
  const [disconnectProvider, { isLoading: isDisconnecting }] = useDisconnectProviderMutation();
  const { data: alpacaSettings, isLoading: isLoadingAlpaca } = useGetAlpacaTradingSettingsQuery(undefined, { skip: !isAuthenticated });
  const [saveAlpacaSettings, { isLoading: isSavingAlpaca }] = useSaveAlpacaTradingSettingsMutation();
  const [deleteAlpacaSettings, { isLoading: isDeletingAlpaca }] = useDeleteAlpacaTradingSettingsMutation();

  // Check for OAuth success/error in URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthSuccess = params.get('oauth_success');
    const providerParam = params.get('provider');

    if (oauthSuccess && providerParam) {
      setProvider(providerParam);
      if (oauthSuccess === 'true') {
        setShowSuccessAlert(true);
      } else {
        setShowErrorAlert(true);
      }

      // Clean up URL parameters
      navigate('/settings', { replace: true });

      // Fetch connected providers after OAuth flow
      refetchProviders();
    }
  }, [location, navigate, refetchProviders]);

  useEffect(() => {
    if (alpacaSettings) {
      reset({ apiKey: alpacaSettings.apiKey || "", apiSecret: "" });
    }
  }, [alpacaSettings, reset]);

  // No need for explicit fetch on mount as useGetConnectedProvidersQuery handles that

  // Handle connect button click
  const handleConnect = (provider) => {
    const returnUrl = `${window.location.origin}/settings`;
    window.location.href = `/api/external-auth/oauth?provider=${provider}&returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  // Handle disconnect button click
  const handleDisconnect = async (provider) => {
    try {
      await disconnectProvider(provider).unwrap();
      refetchProviders(); // Refresh the list after disconnecting
    } catch (error) {
      console.error('Failed to disconnect provider:', error);
    }
  };

  // Check if a provider is connected
  const isProviderConnected = (provider) => {
    return connectedProviders?.includes(provider) || false;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>

      {/* Success Alert */}
      {showSuccessAlert && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setShowSuccessAlert(false)}
        >
          Successfully connected to {provider}!
        </Alert>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setShowErrorAlert(false)}
        >
          Failed to connect to {provider}. Please try again.
        </Alert>
      )}

      {/* Settings Layout with Tabs */}
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
        {/* Left sidebar with tabs */}
        <Box sx={{ 
          borderRight: 1,
          borderColor: 'divider',
          width: 240,
          flexShrink: 0
        }}>
          <Tabs 
            orientation="vertical"
            variant="standard"
            value={0}
            sx={{ 
              borderRight: 1, 
              borderColor: 'divider',
              '& .MuiTabs-indicator': {
                left: 0,
                right: 'auto'
              }
            }}
          >
            <Tab label="Trading Connections" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
          </Tabs>
        </Box>

        {/* Tab content (single section) */}
        <Box id="settings-tabpanel-0" role="tabpanel" aria-labelledby="settings-tab-0" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <SectionHeader variant="h5" component="h2" sx={{ mb: 1.5 }}>
            Trading Connections
          </SectionHeader>
          <Typography variant="body1" paragraph>
            Configure providers used for trading and connected exchange workflows.
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <AccentCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box 
                      component="img" 
                      src="/assets/coinbase-logo.svg" 
                      alt="Coinbase Logo"
                      sx={{ height: 28, width: 28 }}
                    />
                    <SectionHeader variant="h6" component="h3">
                      Coinbase
                    </SectionHeader>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Connect your Coinbase account to enable automated trading workflows.
                  </Typography>
                  <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                    {isLoadingProviders ? (
                      <CircularProgress size={24} />
                    ) : isProviderConnected('Coinbase') ? (
                      <>
                        <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                          Connected
                        </Typography>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          onClick={() => handleDisconnect('Coinbase')}
                          disabled={isDisconnecting}
                        >
                          {isDisconnecting ? <CircularProgress size={24} /> : 'Disconnect'}
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleConnect('Coinbase')}
                      >
                        Connect
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </AccentCard>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <AccentCard sx={{ height: '100%' }}>
                <CardContent>
                  <SectionHeader variant="h6" component="h3" sx={{ mb: 1.5 }}>
                    Alpaca
                  </SectionHeader>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Provide your personal Alpaca API key/secret for trading. Market data still uses the server app settings key.
                  </Typography>
                  <Stack component="form" onSubmit={handleSubmit(async (values) => {
                    try {
                      await saveAlpacaSettings({
                        apiKey: values.apiKey,
                        apiSecret: values.apiSecret || "",
                      }).unwrap();
                      reset({ apiKey: values.apiKey, apiSecret: "" });
                      setAlpacaMessage("Trading credentials saved.");
                    } catch (error) {
                      setAlpacaMessage(error?.data?.message || "Failed to save trading credentials.");
                    }
                  })} spacing={2}>
                    <Controller
                      name="apiKey"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Alpaca API Key"
                          error={!!alpacaErrors.apiKey}
                          helperText={alpacaErrors.apiKey?.message}
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="apiSecret"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Alpaca API Secret"
                          type="password"
                          helperText={alpacaErrors.apiSecret?.message || (alpacaSettings?.hasCredentials ? "Leave blank to keep existing secret." : "")}
                          error={!!alpacaErrors.apiSecret}
                          fullWidth
                        />
                      )}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSavingAlpaca || isLoadingAlpaca}
                      >
                        {isSavingAlpaca ? "Saving..." : "Save Credentials"}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={isDeletingAlpaca || !alpacaSettings?.hasCredentials}
                        onClick={async () => {
                          try {
                            await deleteAlpacaSettings().unwrap();
                            reset({ apiKey: "", apiSecret: "" });
                            setAlpacaMessage("Trading credentials removed.");
                          } catch (error) {
                            setAlpacaMessage(error?.data?.message || "Failed to remove trading credentials.");
                          }
                        }}
                      >
                        {isDeletingAlpaca ? "Removing..." : "Remove Credentials"}
                      </Button>
                    </Stack>
                    {alpacaMessage ? <Alert severity="info">{alpacaMessage}</Alert> : null}
                  </Stack>
                </CardContent>
              </AccentCard>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default SettingsPage;
